<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <div class="mt-3">
    <div class="mt-3">
      <div class="row">
        <p class="bb-sheet-title">PROFILE</p>
      </div>
    </div>
    <!-- ------------------------------------------------------------>
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row"
    >
      <!-- ------------------------------------------------------- -->
      <!--   COL 1: IMAGE/NOTES   -->
      <!-- ---------------------- -->
      <div class="col-md-2 text-center m-3">
        <p class="bb-hdg mt-2 mb-3"></p>
        <div class="row mt-2 mb-3">
          <!-- -------------- -->
          <!-- image          -->
          <!-- -------------- -->
          <div class="form-group text-center">
            <div class="form-floating mb-2">
              <img
                v-bind:src="image"
                height="200"
                width="350"
                class="img-fluid rounded border border-dark bb-image-container"
                alt="character image"
              />
            </div>
            <label
              :class="{ 'bb-click-label-disabled': isProfileLocked }"
              class="btn btn-dark btn-file mb-2 bb-click-label"
            >
              Change Image
              <input
                type="file"
                accept="image/*"
                :disabled="isProfileLocked"
                style="display: none"
                name="image"
                ref="selectImage"
                id="selectImage"
                @change="previewImage"
              />
            </label>
          </div>
        </div>
        <div class="row mt-2 mb-3">
          <!-- ------------------ -->
          <!-- character history  -->
          <!-- ------------------ -->
          <div class="form-group text-center bb-character-history-block">
            <p class="bb-hdg mt-3 mb-3 bb-character-history-title">
              Character History
            </p>
            <div
              class="btn-group-vertical btn-group-lg"
              role="group"
            >
              <button
                v-if="!hasCharacterHistoryDocument"
                type="button"
                :class="{ 'bb-click-label-disabled': isProfileLocked }"
                class="btn btn-primary btn-lg"
                @click="triggerCharacterHistorySelection"
              >
                Add
              </button>
              <button
                v-if="hasCharacterHistoryDocument"
                type="button"
                class="btn btn-primary btn-lg"
                @click="openCharacterHistory"
              >
                View
              </button>
              <button
                v-if="hasCharacterHistoryDocument"
                type="button"
                :class="{ 'bb-click-label-disabled': isProfileLocked }"
                class="btn btn-primary btn-lg"
                @click="triggerCharacterHistorySelection"
              >
                Change
              </button>
              <button
                v-if="hasCharacterHistoryDocument"
                type="button"
                :disabled="isProfileLocked"
                :class="{ 'bb-click-label-disabled': isProfileLocked }"
                class="btn btn-warning btn-lg"
                @click="clearCharacterHistory"
              >
                Clear
              </button>
            </div>
            <input
              type="file"
              accept="application/pdf,.pdf"
              :disabled="isProfileLocked"
              style="display: none"
              name="character_history"
              ref="selectCharacterHistory"
              id="selectCharacterHistory"
              @change="previewCharacterHistory"
            />
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------- -->
      <!--   COL 2: DESCRIPTION    -->
      <!-- ----------------------- -->
      <div class="col-md-4 m-3">
        <p class="bb-hdg mt-2 mb-3">Description</p>
        <div class="row mb-2"></div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--    campaign    -->
          <!-- -------------- -->
          <div class="col-9 form-group">
            <div class="form-floating">
              <template v-if="!shouldUseReadonlyCampaignField">
                <v-select
                  ref="campaign_select"
                  :options="campaign_names"
                  class="form-control"
                  placeholder="select..."
                  v-model="local_character.arena_name"
                  @update:modelValue="handleCampaignChange"
                  id="arena_name"
                  required
                >
                  <template #search="{ attributes, events }">
                    <input
                      class="vs__search"
                      :required="!local_character.arena_name"
                      v-bind="attributes"
                      v-on="events"
                    />
                  </template>
                </v-select>
                <label
                  class="bb-float-label"
                  for="arena_name"
                  >Campaign</label
                >
              </template>
              <template v-else>
                <input
                  readonly
                  class="form-control"
                  :value="local_character.arena_name"
                  id="arena_name"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="arena_name"
                  >Campaign</label
                >
              </template>
            </div>
          </div>
          <!-- --------------------- -->
          <!--   character status    -->
          <!-- --------------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="shouldUseReadonlyStatusField">
                  <input
                    readonly
                    class="form-control"
                    :value="local_character.status"
                    id="status"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="status"
                    >Status</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="statuses"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_character.status"
                    v-bind:class="{
                      'is-invalid': errorFields.includes('status'),
                    }"
                    @update:modelValue="emitUpdate('status', $event)"
                    @keydown="onKeyDown($event)"
                    id="status"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.status"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="status"
                    >Status</label
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--      name      -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="shouldUseReadonlyNameField">
                  <input
                    readonly
                    class="form-control capitalize"
                    :value="local_character.name"
                    id="name"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="name"
                    >Name</label
                  >
                </template>
                <template v-else>
                  <input
                    ref="first_focus"
                    v-model="local_character.name"
                    v-bind:class="{
                      'is-invalid': errorFields.includes('name'),
                    }"
                    id="name"
                    name="name"
                    type="search"
                    placeholder="..."
                    class="form-control capitalize"
                    @keydown="onKeyDown($event)"
                    @input="emitUpdate($event.target.name, $event.target.value)"
                    @mouseup="this.capitaliseHandler('name')"
                    autocomplete="off"
                    required
                  />
                  <label
                    class="bb-float-label"
                    for="name"
                    >Name</label
                  >
                </template>
              </div>
            </div>
          </div>
          <!-- -------------- -->
          <!--     player     -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div
                class="form-floating"
                v-if="this.local_character.category != 'Enemy'"
              >
                <template v-if="shouldUseReadonlyPlayerField">
                  <input
                    readonly
                    class="form-control"
                    :value="formattedSelectedPlayerName"
                    id="player"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="player"
                    >Player</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="player_names"
                    label="label"
                    class="form-control"
                    :modelValue="selectedPlayerOption"
                    placeholder="select..."
                    v-bind:class="{
                      'is-invalid': errorFields.includes('player_name'),
                    }"
                    @keydown="onKeyDown($event)"
                    @update:modelValue="handlePlayerSelectionChange"
                    id="player"
                  >
                  </v-select>
                  <label
                    class="bb-float-label optional"
                    for="player"
                    >Player</label
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--    category    -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="shouldUseReadonlyCategoryField">
                  <input
                    readonly
                    class="form-control"
                    :value="local_character.category"
                    id="category"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="category"
                    >Category</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="categories"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_character.category"
                    v-bind:class="{
                      'is-invalid': errorFields.includes('category'),
                    }"
                    @update:modelValue="emitUpdate('category', $event)"
                    @keydown="onKeyDown($event)"
                    id="category"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.category"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="category"
                    >Category</label
                  >
                </template>
              </div>
            </div>
          </div>
          <!-- -------------- -->
          <!--      type      -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="shouldUseReadonlyTypeField">
                  <input
                    readonly
                    class="form-control"
                    :value="local_character.type"
                    id="type"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="type"
                    >Type</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="types"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_character.type"
                    v-bind:class="{
                      'is-invalid': errorFields.includes('type'),
                    }"
                    @keydown="onKeyDown($event)"
                    @update:modelValue="emitUpdate('type', $event)"
                    id="type"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.type"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="type"
                    >Type</label
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
        <!--  -->
        <div class="row mb-2">
          <!-- -------------- -->
          <!--      race      -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="shouldUseReadonlyRaceField">
                  <input
                    readonly
                    class="form-control"
                    :value="local_character.race_name"
                    id="race"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="race"
                    >Race</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="race_names"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_character.race_name"
                    @keydown="onKeyDown($event)"
                    v-bind:class="{
                      'is-invalid': errorFields.includes('race_name'),
                    }"
                    @update:modelValue="emitUpdate('race_name', $event)"
                    id="race"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.race_name"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="race"
                    >Race</label
                  >
                </template>
              </div>
            </div>
          </div>
          <!-- ------------- -->
          <!--     role      -->
          <!-- ------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="shouldUseReadonlyRoleField">
                  <input
                    readonly
                    class="form-control"
                    :value="local_character.role"
                    id="role"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="role"
                    >Role</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="roles"
                    class="form-control"
                    placeholder="select..."
                    v-model="local_character.role"
                    v-bind:class="{
                      'is-invalid': errorFields.includes('role'),
                    }"
                    @keydown="onKeyDown($event)"
                    @update:modelValue="emitUpdate('role', $event)"
                    id="role"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.role"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="role"
                    >Role</label
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- --------------- -->
          <!--      cults      -->
          <!-- --------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <input
                  readonly
                  class="form-control capitalize"
                  :value="local_character.cults"
                  id="cults"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="cults"
                  >Cults</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <input
                  v-model="local_character.cults"
                  v-bind:class="{
                    'is-invalid': errorFields.includes('cults'),
                  }"
                  id="cults"
                  name="cults"
                  placeholder="..."
                  class="form-control capitalize"
                  @keydown="onKeyDownAllowComma($event)"
                  @input="delayedEmitUpdate('cults', local_character.cults)"
                  autocomplete="off"
                />
                <label
                  class="bb-float-label optional"
                  for="cults"
                  >Cults</label
                >
              </div>
            </div>
          </div>
          <!-- ---------------- -->
          <!--   trades     -->
          <!-- ---------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <input
                  readonly
                  class="form-control capitalize"
                  :value="local_character.trades"
                  id="trades"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="trades"
                  >Trades</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <input
                  v-model="local_character.trades"
                  v-bind:class="{
                    'is-invalid': errorFields.includes('trades'),
                  }"
                  id="trades"
                  name="trades"
                  placeholder="..."
                  class="form-control capitalize"
                  @keydown="onKeyDownAllowComma($event)"
                  @input="delayedEmitUpdate('trades', local_character.trades)"
                  autocomplete="off"
                />
                <label
                  class="bb-float-label optional"
                  for="trades"
                  >Trades</label
                >
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!-- notes          -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <textarea
                  readonly
                  rows="8"
                  class="form-control bb-textarea-with-label bb-profile-notes-control"
                  :value="local_character.notes"
                  id="notes"
                  disabled
                ></textarea>
                <label
                  class="bb-float-label optional"
                  for="notes"
                  >Character Notes</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <textarea
                  id="notes"
                  name="notes"
                  rows="8"
                  class="form-control bb-textarea-with-label bb-profile-notes-control"
                  v-model="local_character.notes"
                  placeholder="..."
                  @input="normaliseLeadingLetterAndDelayedEmitUpdate('notes')"
                  autocomplete="off"
                ></textarea>
                <label
                  class="bb-float-label optional"
                  for="notes"
                  >Character Notes</label
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------- -->
      <!--   COL 3: PHYSICAL  -->
      <!-- --------------------->
      <div class="col-md-2 m-3">
        <p class="bb-hdg mt-2 mb-3">Physical</p>
        <div class="row mb-2"></div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--    gender      -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="isProfileLocked">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_character.gender"
                    id="gender"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="gender"
                    >Gender</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="genders"
                    class="form-control text-center"
                    placeholder="select..."
                    v-model="local_character.gender"
                    @keydown="onKeyDown($event)"
                    @update:modelValue="emitUpdate('gender', $event)"
                    id="gender"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.gender"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="gender"
                    >Gender</label
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--      age       -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <input
                  readonly
                  class="form-control text-center"
                  :value="local_character.age"
                  id="age"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="age"
                  >Age</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <input
                  v-model="local_character.age"
                  v-bind:class="{
                    'is-invalid': errorFields.includes('age'),
                  }"
                  id="age"
                  name="age"
                  placeholder="..."
                  class="form-control text-center"
                  @keydown="onKeyDownNumeric($event)"
                  @input="delayedEmitUpdate('age', local_character.age)"
                  autocomplete="off"
                  required
                />
                <label
                  class="bb-float-label"
                  for="age"
                  >Age</label
                >
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- --------------- -->
          <!--   height (cm)   -->
          <!-- --------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <input
                  readonly
                  class="form-control text-center"
                  :value="local_character.height"
                  id="height"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="height"
                  >Height (cm)</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <input
                  v-model="local_character.height"
                  v-bind:class="{
                    'is-invalid': errorFields.includes('height'),
                  }"
                  id="height"
                  name="height"
                  placeholder="..."
                  class="form-control text-center"
                  @keydown="onKeyDownNumeric($event)"
                  @input="delayedEmitUpdate('height', local_character.height)"
                  autocomplete="off"
                  required
                />
                <label
                  class="bb-float-label"
                  for="height"
                  >Height (cm)</label
                >
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--  weight (kg)   -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <input
                  readonly
                  class="form-control text-center"
                  :value="local_character.weight"
                  id="weight"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="weight"
                  >Weight (kg)</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <input
                  v-model="local_character.weight"
                  v-bind:class="{
                    'is-invalid': errorFields.includes('weight'),
                  }"
                  id="weight"
                  name="weight"
                  placeholder="..."
                  class="form-control text-center"
                  @keydown="onKeyDownNumeric($event)"
                  @input="delayedEmitUpdate('weight', local_character.weight)"
                  autocomplete="off"
                  required
                />
                <label
                  class="bb-float-label"
                  for="weight"
                  >Weight (kg)</label
                >
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--   eye colour   -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="isProfileLocked">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_character.colour_eyes"
                    id="colour_eyes"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="colour_eyes"
                    >Eye Colour</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="colours"
                    class="form-control text-center"
                    placeholder="select..."
                    v-model="local_character.colour_eyes"
                    @keydown="onKeyDown($event)"
                    @update:modelValue="emitUpdate('colour_eyes', $event)"
                    id="colour_eyes"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.colour_eyes"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="colour_eyes"
                    >Eye Colour</label
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- -------------- -->
          <!--  hair colour   -->
          <!-- -------------- -->
          <div class="col">
            <div class="form-group">
              <div class="form-floating">
                <template v-if="isProfileLocked">
                  <input
                    readonly
                    class="form-control text-center"
                    :value="local_character.colour_hair"
                    id="colour_hair"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="colour_hair"
                    >Hair Colour</label
                  >
                </template>
                <template v-else>
                  <v-select
                    :options="colours"
                    class="form-control text-center"
                    placeholder="select..."
                    v-model="local_character.colour_hair"
                    @keydown="onKeyDown($event)"
                    @update:modelValue="emitUpdate('colour_hair', $event)"
                    id="colour_hair"
                    required
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        :required="!local_character.colour_hair"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label"
                    for="colour_hair"
                    >Hair Colour</label
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="local_character.gender == 'Male'"
          class="row mb-2"
        >
          <!-- ---------------- -->
          <!--  cock (inches)   -->
          <!-- ---------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <input
                  readonly
                  class="form-control text-center"
                  :value="local_character.cock"
                  id="cock"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="cock"
                  >Cock (inches)</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <input
                  v-model="local_character.cock"
                  v-bind:class="{
                    'is-invalid': errorFields.includes('cock'),
                  }"
                  id="cock"
                  name="cock"
                  placeholder="..."
                  class="form-control text-center"
                  @keydown="onKeyDownNumeric($event)"
                  @input="emitUpdate($event.target.name, $event.target.value)"
                  autocomplete="off"
                  required
                />
                <label
                  class="bb-float-label"
                  for="cock"
                  >Cock (inches)</label
                >
              </div>
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <!-- --------------- -->
          <!--      marks      -->
          <!-- --------------- -->
          <div class="col">
            <div class="form-group">
              <div
                v-if="isProfileLocked"
                class="form-floating"
              >
                <input
                  readonly
                  class="form-control bb-profile-notes-control"
                  :value="local_character.distinguishing_marks"
                  id="distinguishing_marks"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="distinguishing_marks"
                  >Distinguishing Marks</label
                >
              </div>
              <div
                v-else
                class="form-floating"
              >
                <input
                  v-model="local_character.distinguishing_marks"
                  v-bind:class="{
                    'is-invalid': errorFields.includes('distinguishing_marks'),
                  }"
                  id="distinguishing_marks"
                  name="distinguishing_marks"
                  placeholder="..."
                  class="form-control bb-profile-notes-control"
                  @keydown="onKeyDown($event)"
                  @input="
                    capitaliseAndDelayedEmitUpdate('distinguishing_marks')
                  "
                  autocomplete="off"
                />
                <label
                  class="bb-float-label optional"
                  for="distinguishing_marks"
                  >Distinguishing Marks</label
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------- -->
      <!--   COL 4: SUMMARY   -->
      <!-- --------------------->
      <div class="col-md-4 m-3">
        <SheetSummary :character="character" />
      </div>
    </div>
  </div>
</template>

<script>
import SheetSummary from './SheetSummary.vue'
import { axios } from '@/common/api.service.js'
import showNotification from '../helpers/showNotification'

import getStringAfterLastSlash from '../helpers/getStringAfterLastSlash'
import capitaliseFirstLetter from '../helpers/capitaliseFirstLetter'
import normaliseLeadingLetterText from '../helpers/normaliseLeadingLetterText'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import isAlphanumeric from '../helpers/isAlphanumeric'
import getRaceDetails from '../helpers/getRaceDetails'
import extractNames from '../helpers/extractNames'
import isNumeric from '../helpers/isNumeric'

import defaultImage from '../assets/images/default-character-image.png'

import { mapActions, mapGetters } from 'vuex'
import { inject } from 'vue'

export default {
  name: 'CharacterSheetProfile',
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    listScope: {
      type: String,
      required: false,
      default: 'campaign',
    },
  },
  //========================================================
  // components
  //------------
  components: {
    SheetSummary,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // data
  //------
  data() {
    return {
      defaultImage,
      //
      local_character: null,
      //
      original_race_name: '',
      original_name: '',

      default_race: '',
      //
      // per-field debounce timers (key -> timeout id)
      pendingTimers: {},
      //
      original_roles: [],
      player_names: [],
      damage_mods: [],
      statuses: [],
      categories: [],
      race_names: [],
      genders: [],
      colours: [],
      types: [],
      races: [],
      roles: [],
      my_campaigns: [],
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    //----------------------
    // watch lookup payloads
    //----------------------
    character_lookups: {
      deep: true,
      handler() {
        this.getDropDownValues()
      },
    },
    //------------
    // watch race
    //------------
    watch_race() {
      this.onRaceChange()
    },
    //-----------------------
    // watch local character
    //-----------------------
    local_character: {
      deep: true,
      handler: function (newVal) {
        console.log('=====================================')
        console.log('WATCH PROFILE LOCAL CHARACTER....')
        //-----------------------------
        // ensure gender matches roles
        //-----------------------------
        if (newVal.gender == 'Female') {
          this.roles = this.original_roles.filter(
            (r) => r != 'Sorcerer' && r != 'Priest',
          )
        } else if (newVal.gender == 'Male') {
          this.roles = this.original_roles.filter(
            (r) => r != 'Sorcereress' && r != 'Priestess',
          )
        } else {
          this.roles = this.original_roles
        }
      },
    },
    //-----------------
    // watch character
    //-----------------
    character: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (!newVal) return
        const previousPreviewUrl =
          this.local_character?.character_history_preview_url ?? null
        //---------------------
        // get dropdown values
        //---------------------
        this.getDropDownValues()
        //---------------------------------------------------------------
        // load local character every time character changes from parent
        //---------------------------------------------------------------
        this.local_character = copyObjectNoRef(newVal)
        if (
          previousPreviewUrl !==
          this.local_character?.character_history_preview_url
        ) {
          this.revokeCharacterHistoryPreviewUrl(previousPreviewUrl)
        }
        //------------------------------------------------------------
        // Set original values IMMEDIATELY to prevent race condition
        // (before watch_race fires and triggers onRaceChange)
        //------------------------------------------------------------
        if (this.original_race_name === '') {
          this.original_race_name = newVal.race_name
        }
        if (this.original_name === '') {
          this.original_name = newVal.name
        }

        // console.log('body type = ', this.local_character.body_type)
        //-----------------------------------
        // set capitalise after special char
        //-----------------------------------
        if (this.local_character.name != null) {
          this.capitaliseAfterSpecialChar()
        }

        this.applyScopeDefaults()
      },
    },
    currentPlayerName(newVal, oldVal) {
      if (!newVal || newVal === oldVal) {
        return
      }

      this.applyScopeDefaults()
    },
  },
  //========================================================
  // computed
  //-----------
  computed: {
    //---------------
    // character lookups
    //----------------
    character_lookups() {
      return this.getCharacterLookups() || {}
    },
    //-----------------------
    // check if race changed
    //-----------------------
    watch_race() {
      if (this.isReady) {
        return this.local_character.race_name
      }
      return ''
    },
    //---------
    // isReady
    //---------
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    isMyCharactersScope() {
      return this.listScope === 'mine'
    },
    isExistingCharacter() {
      return (
        this.isReady &&
        (Number(this.local_character.id) > 0 ||
          String(this.local_character.slug || '').trim().length > 0)
      )
    },
    isProfileLocked() {
      return this.isReady && this.local_character.status !== 'Alive'
    },
    isNewMyCharactersSheet() {
      return (
        this.isMyCharactersScope && this.isReady && !this.isExistingCharacter
      )
    },
    shouldUseReadonlyCampaignField() {
      return this.isProfileLocked || !this.isMyCharactersScope
    },
    shouldUseReadonlyPlayerField() {
      return this.isProfileLocked || this.isMyCharactersScope
    },
    shouldUseReadonlyNameField() {
      return this.isProfileLocked
    },
    shouldUseReadonlyCategoryField() {
      return this.isProfileLocked || this.isNewMyCharactersSheet
    },
    shouldUseReadonlyTypeField() {
      return this.isProfileLocked || this.isNewMyCharactersSheet
    },
    shouldUseReadonlyStatusField() {
      return this.isNewMyCharactersSheet
    },
    shouldUseReadonlyRaceField() {
      return (
        this.isProfileLocked ||
        (this.isMyCharactersScope && this.isExistingCharacter)
      )
    },
    shouldUseReadonlyRoleField() {
      return (
        this.isProfileLocked ||
        (this.isMyCharactersScope && this.isExistingCharacter)
      )
    },
    currentPlayerName() {
      const userProfile = this.getUserProfile()
      return this.buildPlayerOption(userProfile)?.value || ''
    },
    currentPlayerLabel() {
      const userProfile = this.getUserProfile()
      return this.buildPlayerOption(userProfile)?.label || ''
    },
    selectedPlayerOption() {
      return this.findPlayerOption({
        playerId: this.local_character?.player,
        playerName: this.local_character?.player_name,
      })
    },
    formattedSelectedPlayerName() {
      if (
        this.isNewMyCharactersSheet &&
        this.local_character?.player_name === this.currentPlayerName &&
        this.currentPlayerLabel
      ) {
        return this.currentPlayerLabel
      }

      return this.getPlayerLabel(this.local_character?.player_name)
    },
    campaign_names() {
      return extractNames(this.my_campaigns)
    },
    //------
    // image
    //-------
    image() {
      if (this.isReady && this.local_character.image != null) {
        return this.local_character.image
      } else {
        return this.defaultImage
      }
    },
    hasCharacterHistoryDocument() {
      return Boolean(
        this.local_character?.character_history_preview_url ||
          this.local_character?.character_history_file ||
          this.local_character?.character_history,
      )
    },
    characterHistoryViewUrl() {
      return (
        this.local_character?.character_history_preview_url ||
        this.local_character?.character_history ||
        ''
      )
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
      getUserProfile: 'getUserProfile',
    }),
    ...mapGetters('combat', {
      getPlayerNames: 'getPlayerNames',
      getCampaign: 'getCampaign',
      getCampaignPlayers: 'getCampaignPlayers',
    }),
    ...mapActions('combat', {
      loadCampaignPlayers: 'loadCampaignPlayers',
    }),
    ...mapGetters('characters', {
      getCharacterLookups: 'getCharacterLookups',
    }),
    ...mapGetters('races', {
      getCharacterRaces: 'getCharacterRaces',
    }),
    resolveSelectedCampaign() {
      const allowedCampaigns = Array.isArray(this.my_campaigns)
        ? this.my_campaigns
        : []

      return (
        allowedCampaigns.find(
          (entry) =>
            String(entry?.id) === String(this.local_character?.arena) ||
            entry?.name === this.local_character?.arena_name,
        ) || null
      )
    },
    membershipsMatchSelectedCampaign(memberships = []) {
      if (!Array.isArray(memberships) || memberships.length === 0) {
        return false
      }

      const selectedCampaignId =
        this.resolveSelectedCampaign()?.id ??
        this.local_character?.arena ??
        null

      if (selectedCampaignId == null || selectedCampaignId === '') {
        return false
      }

      return memberships.some(
        (entry) => String(entry?.arena) === String(selectedCampaignId),
      )
    },
    async ensureCampaignPlayersLoaded() {
      const existingMemberships = this.getCampaignPlayers() || []

      if (
        Array.isArray(existingMemberships) &&
        existingMemberships.length > 0 &&
        this.membershipsMatchSelectedCampaign(existingMemberships)
      ) {
        return existingMemberships
      }

      const selectedCampaign = this.resolveSelectedCampaign()
      let campaignSlug = selectedCampaign?.slug || null

      if (!campaignSlug && !this.isMyCharactersScope) {
        campaignSlug = this.getCampaign()?.slug || null
      }

      if (!campaignSlug) {
        return []
      }

      try {
        await this.loadCampaignPlayers(campaignSlug)
      } catch (error) {
        console.error('Failed to load campaign memberships:', error)
      }

      return this.getCampaignPlayers() || []
    },
    findMatchingMembership(player = {}) {
      const membershipEntries = this.getCampaignPlayers() || []

      if (!Array.isArray(membershipEntries) || membershipEntries.length === 0) {
        return null
      }

      const userId = player?.id == null ? null : String(player.id)
      const userName = String(player?.user_name || '')
        .trim()
        .toLowerCase()
      const firstName = String(player?.first_name || '')
        .trim()
        .toLowerCase()

      if (userId) {
        const idMatch = membershipEntries.find(
          (entry) => String(entry?.player) === userId,
        )

        if (idMatch) {
          return idMatch
        }
      }

      if (userName) {
        const userNameMatch = membershipEntries.find(
          (entry) =>
            String(entry?.user_name || '')
              .trim()
              .toLowerCase() === userName,
        )

        if (userNameMatch) {
          return userNameMatch
        }
      }

      if (firstName) {
        const firstNameMatches = membershipEntries.filter(
          (entry) =>
            String(entry?.first_name || '')
              .trim()
              .toLowerCase() === firstName,
        )

        if (firstNameMatches.length === 1) {
          return firstNameMatches[0]
        }
      }

      return null
    },
    //========================================================
    // preview image
    //---------------
    previewImage(event) {
      if (this.isProfileLocked) return
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        this.local_character.image = e.target.result
        // create image file for uploading
        const formData = new FormData()
        formData.append('image', file)
        this.local_character.image_file = formData
        this.emitUpdate('image', e.target.result) // emit data URL
        this.emitUpdate('image_file', formData) // emit formdata
        this.resetImageInput()
      }
      reader.readAsDataURL(file)
    },
    previewCharacterHistory(event) {
      if (this.isProfileLocked) return
      const file = event.target.files[0]

      if (!file) return

      const hadExistingCharacterHistory = Boolean(
        this.local_character.character_history_preview_url ||
          this.local_character.character_history_file ||
          this.local_character.character_history,
      )

      this.revokeCharacterHistoryPreviewUrl()

      const previewUrl = URL.createObjectURL(file)
      const formData = new FormData()
      formData.append('character_history', file)

      this.local_character.character_history_preview_url = previewUrl
      this.local_character.character_history_file = formData
      this.local_character.character_history_name = file.name

      this.emitUpdate('character_history_preview_url', previewUrl)
      this.emitUpdate('character_history_name', file.name)
      this.emitUpdate('character_history_file', formData)
      this.resetCharacterHistoryInput()
      this.queueCharacterHistoryNotification(
        hadExistingCharacterHistory
          ? 'Character History changed.'
          : 'Character History added.',
      )
    },
    resetImageInput() {
      const imageInput = this.$refs?.selectImage

      if (imageInput) {
        imageInput.value = ''
      }
    },
    resetCharacterHistoryInput() {
      const historyInput = this.$refs?.selectCharacterHistory

      if (historyInput) {
        historyInput.value = ''
      }
    },
    triggerCharacterHistorySelection() {
      if (this.isProfileLocked) return

      this.$refs?.selectCharacterHistory?.click()
    },
    revokeCharacterHistoryPreviewUrl(
      url = this.local_character?.character_history_preview_url,
    ) {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        URL.revokeObjectURL(url)
      }
    },
    clearCharacterHistory() {
      if (this.isProfileLocked) return

      const hadPersistedCharacterHistory = Boolean(
        this.character?.character_history,
      )

      this.revokeCharacterHistoryPreviewUrl()
      this.local_character.character_history_preview_url = null
      this.local_character.character_history_file = null
      this.local_character.character_history_name = null
      this.local_character.character_history = null

      this.emitUpdate('character_history_preview_url', null)
      this.emitUpdate('character_history_name', null)
      this.emitUpdate('character_history_file', null)

      if (hadPersistedCharacterHistory) {
        this.emitUpdate('character_history', null)
      }

      this.resetCharacterHistoryInput()
      this.queueCharacterHistoryNotification('Character History cleared.')
    },
    queueCharacterHistoryNotification(message) {
      setTimeout(() => {
        showNotification(message)
      }, 0)
    },
    openCharacterHistory() {
      const rawUrl = this.characterHistoryViewUrl

      if (!rawUrl) {
        return
      }

      const resolvedUrl =
        rawUrl.startsWith('blob:') || /^https?:\/\//i.test(rawUrl)
          ? rawUrl
          : new URL(
              rawUrl,
              import.meta.env.VITE_API_URL || window.location.origin,
            ).toString()

      const historyWindow = window.open('', '_blank')

      if (historyWindow) {
        historyWindow.opener = null
        historyWindow.location = resolvedUrl
        return
      }

      const historyLink = document.createElement('a')
      historyLink.href = resolvedUrl
      historyLink.target = '_blank'
      historyLink.rel = 'noopener noreferrer'
      document.body.appendChild(historyLink)
      historyLink.click()
      document.body.removeChild(historyLink)
    },
    //========================================================
    // preload image
    //---------------
    preloadImage(image) {
      let image_name = getStringAfterLastSlash(image)
      //-----------------------------
      // get blob from src url fetch
      //-----------------------------
      fetch(image)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          return response.blob()
        })
        .then((blob) => {
          if (this.local_character?.image !== image) {
            return
          }
          //---------------------------
          // create FormData with blob
          //---------------------------
          const formData = new FormData()
          formData.append('image', blob, image_name)
          //----------------------------
          // emit image_file (FormData)
          //----------------------------
          this.emitUpdate('image_file', formData)
        })
        .catch((error) => {
          console.error('Failed to fetch and convert race image:', error)
        })
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      if (this.isProfileLocked && key !== 'status') return
      console.log('emitUpdate: ' + key + ' = ' + value)
      // clear error for this field when value changes
      this.clearError(key)
      this.$emit('update', { type: 'profile', key: key, value: value })
    },
    //========================================================
    // delayed emit update
    //---------------------
    delayedEmitUpdate(key, value, delay = 600) {
      if (this.isProfileLocked && key !== 'status') return
      //-----------------------------------
      // clear existing timer for this key
      //-----------------------------------
      if (this.pendingTimers[key]) {
        clearTimeout(this.pendingTimers[key])
      }
      //---------------
      // schedule emit
      //---------------
      this.pendingTimers[key] = setTimeout(() => {
        this.emitUpdate(key, value)
        delete this.pendingTimers[key]
      }, delay)
    },
    //========================================================
    // apply scope defaults
    //---------------------
    applyScopeDefaults() {
      if (!this.isMyCharactersScope || !this.isReady) {
        return
      }

      if (
        !this.isExistingCharacter &&
        this.currentPlayerName &&
        this.local_character.player_name !== this.currentPlayerName
      ) {
        this.local_character.player_name = this.currentPlayerName
        this.emitUpdate('player_name', this.local_character.player_name)
      }

      if (!this.isExistingCharacter) {
        this.local_character.category = 'Party'
        this.local_character.type = 'Character'
        this.categories = ['Party']
        this.types = ['Character']
        this.syncSelectedCampaignToMemberships()
      } else {
        this.getDropDownValues()
      }
    },
    syncSelectedCampaignToMemberships() {
      if (
        !this.isMyCharactersScope ||
        !this.isReady ||
        this.isExistingCharacter
      ) {
        return
      }

      const allowedCampaigns = Array.isArray(this.my_campaigns)
        ? this.my_campaigns
        : []

      if (allowedCampaigns.length === 0) {
        this.local_character.arena_name = ''
        this.local_character.arena = null
        this.emitUpdate('arena_name', '')
        this.emitUpdate('arena', null)
        return
      }

      const selectedCampaign = allowedCampaigns.find(
        (entry) => entry.name === this.local_character.arena_name,
      )

      if (selectedCampaign) {
        this.local_character.arena = selectedCampaign.id || null
        this.emitUpdate('arena', this.local_character.arena)
        return
      }

      const [defaultCampaign] = allowedCampaigns
      this.local_character.arena_name = defaultCampaign?.name || ''
      this.local_character.arena = defaultCampaign?.id || null
      this.emitUpdate('arena_name', this.local_character.arena_name)
      this.emitUpdate('arena', this.local_character.arena)
    },
    //========================================================
    // load my campaigns
    //------------------
    async loadMyCampaigns() {
      if (!this.isMyCharactersScope) {
        return
      }

      const headers = this.getHeaders()
      const campaigns = []
      let nextUrl = 'api/v1/campaigns/member/'

      try {
        while (nextUrl) {
          const response = await axios.get(nextUrl, {
            headers,
            mode: 'same-origin',
          })

          const payload = response.data
          const pageResults = payload?.results ?? payload

          if (Array.isArray(pageResults)) {
            campaigns.push(...pageResults)
          }

          nextUrl = payload?.next ?? null
        }

        this.my_campaigns = campaigns
        this.syncSelectedCampaignToMemberships()
      } catch (error) {
        console.error('Failed to load my campaigns:', error)
      }
    },
    //========================================================
    // handle campaign change
    //-----------------------
    async handleCampaignChange(value) {
      const campaign = this.my_campaigns.find((entry) => entry.name === value)

      this.local_character.arena_name = campaign?.name || value || ''
      this.local_character.arena = campaign?.id || null
      this.local_character.player_name = this.currentPlayerName

      this.emitUpdate('arena_name', this.local_character.arena_name)
      this.emitUpdate('arena', this.local_character.arena)
      this.emitUpdate('player_name', this.local_character.player_name)

      if (!campaign?.slug) {
        this.local_character.player = null
        this.emitUpdate('player', null)
        return
      }

      try {
        await this.loadCampaignPlayers(campaign.slug)
        const campaignPlayers =
          this.$store.getters['combat/getCampaignPlayers'] || []
        const userProfile = this.getUserProfile()
        const membership = campaignPlayers.find(
          (entry) => String(entry.player) === String(userProfile?.id),
        )

        this.local_character.player = membership?.id || null
        this.emitUpdate('player', this.local_character.player)
      } catch (error) {
        console.error('Failed to load campaign players:', error)
        this.emitUpdate('player', null)
      }
    },
    //========================================================
    // build player option
    //--------------------
    buildPlayerOption(player = {}) {
      const matchingMembership = this.findMatchingMembership(player)

      const userName = (
        player?.user_name ||
        matchingMembership?.user_name ||
        player?.player_name ||
        ''
      )
        .toString()
        .trim()
      const firstName = (
        player?.first_name ||
        matchingMembership?.first_name ||
        ''
      )
        .toString()
        .trim()
      const fallbackValue = userName || firstName
      const isMembershipEntry =
        player?.player != null ||
        player?.player_email != null ||
        player?.player_name != null

      if (!fallbackValue) {
        return null
      }

      return {
        value: fallbackValue,
        label: firstName ? `${userName} (${firstName})` : fallbackValue,
        firstName,
        userId: player?.id || matchingMembership?.player || null,
        membershipId: isMembershipEntry ? player?.id || null : null,
      }
    },
    findPlayerOption({ playerId = null, playerName = '' } = {}) {
      const options = this.player_names || []

      if (playerId != null) {
        const directMatch = options.find(
          (option) => String(option?.membershipId) === String(playerId),
        )

        if (directMatch) {
          return directMatch
        }

        const memberships = this.getCampaignPlayers() || []
        const membership = memberships.find(
          (entry) => String(entry?.id) === String(playerId),
        )

        if (membership) {
          const relationMatch = options.find(
            (option) =>
              String(option?.userId) === String(membership?.player) ||
              option?.value === membership?.user_name ||
              option?.value === membership?.player_name,
          )

          if (relationMatch) {
            return relationMatch
          }
        }
      }

      if (!playerName) {
        return null
      }

      return (
        options.find(
          (option) =>
            option?.value === playerName || option?.firstName === playerName,
        ) || null
      )
    },
    //========================================================
    // get player label
    //-----------------
    getPlayerLabel(value) {
      const matchedOption = this.findPlayerOption({
        playerId: this.local_character?.player,
        playerName: value,
      })

      if (matchedOption?.label) {
        return matchedOption.label
      }

      const memberships = this.getCampaignPlayers() || []
      const matchedMembership = memberships.find((entry) => {
        if (
          this.local_character?.player != null &&
          String(entry?.id) === String(this.local_character.player)
        ) {
          return true
        }

        return (
          entry?.user_name === value ||
          entry?.first_name === value ||
          entry?.player_name === value
        )
      })

      if (matchedMembership) {
        return this.buildPlayerOption(matchedMembership)?.label || value || ''
      }

      return value || ''
    },
    //========================================================
    // handle player selection change
    //-------------------------------
    async handlePlayerSelectionChange(option) {
      const selectedValue = option?.value || ''
      const memberships = await this.ensureCampaignPlayersLoaded()
      const selectedMembershipId =
        option?.membershipId ||
        memberships.find(
          (entry) =>
            String(entry?.player) === String(option?.userId) ||
            String(entry?.player_email || '') === String(option?.userId),
        )?.id ||
        null

      this.local_character.player_name = selectedValue
      this.local_character.player = selectedMembershipId
      this.emitUpdate('player_name', selectedValue)
      this.emitUpdate('player', this.local_character.player)
    },
    //========================================================
    // sync selected player name
    //--------------------------
    syncSelectedPlayerName() {
      const matchedOption = this.findPlayerOption({
        playerId: this.local_character?.player,
        playerName: this.local_character?.player_name,
      })

      if (!matchedOption) {
        return
      }
    },
    //========================================================
    // get dropdown values
    //---------------------
    getDropDownValues() {
      const lookups = this.getCharacterLookups() || {}

      this.statuses = lookups.status || []
      this.categories = lookups.category || []
      this.types = lookups.type || []
      this.genders = lookups.gender || []
      this.roles = lookups.role || []
      this.colours = lookups.colour_eyes || []

      this.races = this.getCharacterRaces()
      //
      this.race_names = extractNames(this.races)
      const campaignPlayers = this.getCampaignPlayers() || []
      const selectedCampaignPlayers = this.membershipsMatchSelectedCampaign(
        campaignPlayers,
      )
        ? campaignPlayers
        : []
      const arenaPlayers = this.isMyCharactersScope
        ? []
        : this.getCampaign()?.players || []
      const playerOptionSource =
        selectedCampaignPlayers.length > 0
          ? selectedCampaignPlayers
          : arenaPlayers

      this.player_names = playerOptionSource
        .map((player) => this.buildPlayerOption(player))
        .filter(Boolean)

      if (this.player_names.length === 0) {
        this.player_names = (this.getPlayerNames() || [])
          .map((playerName) => {
            if (!playerName) {
              return null
            }

            return {
              value: playerName,
              label: playerName,
              firstName: playerName,
            }
          })
          .filter(Boolean)
      }

      this.syncSelectedPlayerName()
      //
      this.original_roles = this.roles
    },
    //========================================================
    // capitalise handler
    //--------------------
    capitaliseHandler(field) {
      this.local_character[field] = capitaliseFirstLetter(
        this.local_character[field],
      )
    },
    //========================================================
    // capitalise then emit update
    //-----------------------------
    capitaliseAndDelayedEmitUpdate(field, delay = 600) {
      this.capitaliseHandler(field)
      this.delayedEmitUpdate(field, this.local_character[field], delay)
    },
    //========================================================
    // normalise leading letter then emit update
    //-------------------------------------------
    normaliseLeadingLetterAndDelayedEmitUpdate(field, delay = 600) {
      this.local_character[field] = normaliseLeadingLetterText(
        this.local_character[field],
      )
      this.delayedEmitUpdate(field, this.local_character[field], delay)
    },
    //========================================================
    // on race change
    //----------------
    onRaceChange() {
      //---------------------------------------------------------
      // check race name change OR initial load of new character
      //---------------------------------------------------------
      if (
        this.local_character.race_name != this.original_race_name ||
        (this.local_character.id == 0 && !this.local_character.image)
      ) {
        //------------------
        // get race details
        //------------------
        let raceDetails = getRaceDetails(
          this.local_character.race_name,
          this.races,
        )
        //-----------------------
        // load new race details
        //-----------------------
        if (raceDetails) {
          //----------------
          // got race image
          //----------------
          if (raceDetails.image) {
            const hasSelectedCustomImage =
              this.local_character?.image_file &&
              typeof this.local_character?.image === 'string' &&
              this.local_character.image.startsWith('data:')

            if (hasSelectedCustomImage) {
              this.original_race_name = this.local_character.race_name
              return
            }
            //---------------------------
            // clear existing image_file
            //---------------------------
            this.local_character.image = raceDetails.image
            this.local_character.image_file = null
            this.resetImageInput()
            //-------------
            // emit update
            // ------------
            this.emitUpdate('image', raceDetails.image)
            this.preloadImage(raceDetails.image)
          }
        }
      }
      //--------------------------
      // reset original race name
      //--------------------------
      this.original_race_name = this.local_character.race_name
    },
    //======================================================
    // capitalise after special char
    //-------------------------------
    capitaliseAfterSpecialChar() {
      //--------------------------
      // set name as target field
      //--------------------------
      let el = document.getElementById('name')
      if (el) {
        //------------------------
        // listen to name keydown
        //------------------------
        el.addEventListener('keydown', function (e) {
          //--------------------
          // ignore command key
          //--------------------
          if (
            e.key !== 'Delete' &&
            e.key !== 'Backspace' &&
            !e.shiftKey &&
            !e.ctrlKey &&
            !e.option &&
            !e.altKey
          ) {
            let fieldVal = String(e.target.value)
            //---------------------
            // check previous char
            //---------------------
            let prevChar = fieldVal.charAt(e.target.value.length - 1)
            //------------------------------------
            // uc this char if prev = apos/hyphen
            //------------------------------------
            if (prevChar == "'" || prevChar == '-') {
              let key = e.key.toUpperCase()
              e.target.value = e.target.value + key
              e.preventDefault()
            }
          }
        })
      }
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },
    //========================================================
    // on key down allow comma
    //-------------------------
    onKeyDownAllowComma(e) {
      if (e.key === ',') return
      this.onKeyDown(e)
    },
    //========================================================
    // on key down numeric
    //---------------------
    onKeyDownNumeric(e) {
      if (!isNumeric(e)) e.preventDefault()
    },
    //========================================================
    // force initial focus
    //---------------------
    forceInitialFocus() {
      this.editingMode = true
      this.$nextTick(() => {
        if (this.isNewMyCharactersSheet) {
          const campaignInput =
            this.$refs.campaign_select?.$el?.querySelector('input')
          if (campaignInput) {
            campaignInput.focus()
            return
          }
        }

        this.$refs.first_focus.focus()
      })
    },
    //========================================================
    // clear error
    //-------------
    clearError(fieldName) {
      this.$emit('clear-error', fieldName)
    },
  },
  //========================================================
  // mounted
  //---------
  async mounted() {
    await this.loadMyCampaigns()
    await this.ensureCampaignPlayersLoaded()
    this.getDropDownValues()
    //-------------------------------------------------
    // position cursor on name field for new character
    //-------------------------------------------------
    if (this.local_character.id == 0) this.forceInitialFocus()
  },
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    // clear any outstanding timers
    Object.values(this.pendingTimers || {}).forEach((t) => clearTimeout(t))
    this.pendingTimers = {}
    this.revokeCharacterHistoryPreviewUrl()
  },
}
</script>

<style scoped>
.form-floating .bb-profile-notes-control {
  font-size: 1.4rem !important;
  line-height: 1.35 !important;
}

.bb-click-label-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.bb-character-history-title {
  margin-bottom: 1.6rem !important;
}

.bb-character-history-block .btn-group-vertical > .btn {
  min-width: 7.5rem;
}
</style>
